import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getSite } from '../content/index.js';
import { scrollToUniversities } from '../utils/scroll.js';
import BrandLogo from './BrandLogo.jsx';
import { useFindCourse } from './FindCourseContext.js';

export default function Header({ onProgramNavigate }) {
  const site = getSite();
  const navigate = useNavigate();
  const location = useLocation();
  const [featured, setFeatured] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { openFindCourse } = useFindCourse();

  function handleViewAllUniversities(event) {
    event.preventDefault();
    setDropdownOpen(false);
    setMobileOpen(false);

    if (location.pathname === '/') {
      scrollToUniversities();
      window.history.replaceState(null, '', '/#universities');
      return;
    }

    navigate({ pathname: '/', hash: 'universities' });
  }

  function handleProgramClick(item) {
    setMobileOpen(false);
    if (onProgramNavigate) {
      onProgramNavigate(item);
      return;
    }
    const params = new URLSearchParams();
    if (item.search) params.set('search', item.search);
    if (item.level) params.set('level', item.level);
    navigate({ pathname: '/', search: params.toString(), hash: 'universities' });
  }

  useEffect(() => {
    async function loadFeatured() {
      try {
        const res = await fetch('/api/universities/featured');
        if (!res.ok) return;
        const payload = await res.json();
        setFeatured(payload.data?.universities || []);
      } catch {
        setFeatured([]);
      }
    }

    loadFeatured();
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }

    function handleEscape(event) {
      if (event.key === 'Escape') {
        setDropdownOpen(false);
        setMobileOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  return (
    <header className={`header ${mobileOpen ? 'mobile-open' : ''}`}>
      <div className="container header-inner">
        <Link to="/" className="logo logo-link" onClick={() => setMobileOpen(false)}>
          <BrandLogo brand={site.brand} />
        </Link>

        <button
          type="button"
          className="nav-toggle"
          aria-expanded={mobileOpen}
          aria-label="Toggle navigation"
          onClick={() => setMobileOpen((open) => !open)}
        >
          <span />
          <span />
          <span />
        </button>

        <nav className={`nav ${mobileOpen ? 'open' : ''}`}>
          {(site.navPrograms || []).map((item) => (
            <button
              key={item.label}
              type="button"
              className="nav-link nav-program"
              onClick={() => handleProgramClick(item)}
            >
              {item.label}
            </button>
          ))}

          <div className="nav-dropdown" ref={dropdownRef}>
            <button
              type="button"
              className={`nav-link nav-dropdown-trigger ${dropdownOpen ? 'open' : ''}`}
              onClick={() => setDropdownOpen((open) => !open)}
              aria-expanded={dropdownOpen}
              aria-haspopup="true"
            >
              Universities
              <span className="nav-dropdown-chevron" aria-hidden>
                ▾
              </span>
            </button>

            {dropdownOpen && (
              <div className="nav-dropdown-menu" role="menu">
                {featured.length === 0 && (
                  <p className="nav-dropdown-empty">No universities available</p>
                )}
                {featured.map((university) => (
                  <Link
                    key={university.id}
                    to={`/universities/${university.slug}`}
                    className="nav-dropdown-item"
                    role="menuitem"
                    onClick={() => {
                      setDropdownOpen(false);
                      setMobileOpen(false);
                    }}
                  >
                    {university.shortName || university.name}
                  </Link>
                ))}
                <a
                  href="/#universities"
                  className="nav-dropdown-item nav-dropdown-item-muted"
                  role="menuitem"
                  onClick={handleViewAllUniversities}
                >
                  View all universities
                </a>
              </div>
            )}
          </div>

          <button
            type="button"
            className="nav-link nav-program"
            onClick={() => {
              setMobileOpen(false);
              openFindCourse();
            }}
          >
            Find my course
          </button>

          <a
            href="#counsel"
            className="nav-link primary"
            onClick={() => setMobileOpen(false)}
          >
            {site.ctaCounsellor}
          </a>
        </nav>
      </div>
    </header>
  );
}
